import { useCallback, useEffect, useRef, useState } from "react";
import AssetTypeForm from "./AssetTypeForm";
import AssetTypeTable from "./AssetTypeTable";
import Header from "../../../components/Header";
import "bootstrap/dist/css/bootstrap.min.css";
import { toast } from "react-hot-toast";
import {
  addAssetType,
  deleteAssetType,
  editAssetType,
  getPaginatedAssetTypes,
} from "../../../services/ams/assetTypeService";
import { getAssetCategoryCombo } from "../../../services/ams/assetCategoryService";
import { getAssignmentTypesCombo } from "../../../services/ams/assignmentTypeService";

const AssetType = () => {
  const [assetTypes, setAssetTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalCount, setTotalCount] = useState(0);

  const [assetCategories, setAssetCategories] = useState([]);
  const [assignmentTypes, setAssignmentTypes] = useState([]);

  const [openForm, setOpenForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const [importResult, setImportResult] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [duplicateRecords, setDuplicateRecords] = useState([]);

  const fileInputRef = useRef(null);

  const extractAssetTypesFromResponse = (response) => {
    const root = response?.data;
    const payload = root?.data ?? root;

    const list =
      payload?.rows ??
      payload?.items ??
      payload?.asset_types ??
      payload?.data ??
      (Array.isArray(payload) ? payload : []);

    const total =
      payload?.totalCount ??
      payload?.total ??
      payload?.count ??
      payload?.pagination?.total ??
      (Array.isArray(list) ? list.length : 0);

    return {
      list: Array.isArray(list) ? list : [],
      total: Number.isFinite(Number(total)) ? Number(total) : 0,
    };
  };

  const extractComboListFromResponse = (response) => {
    const root = response?.data;
    const payload = root?.data ?? root;

    const list =
      payload?.rows ??
      payload?.items ??
      payload?.data ??
      (Array.isArray(payload) ? payload : []);

    return Array.isArray(list) ? list : [];
  };

  const fetchAssetTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPaginatedAssetTypes(currentPage, itemsPerPage);
      const { list, total } = extractAssetTypesFromResponse(response);
      setAssetTypes(list);
      setTotalCount(total);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to fetch asset types");
      setAssetTypes([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  const fetchAssetCategories = useCallback(async () => {
    try {
      const response = await getAssetCategoryCombo(["id", "asset_category"]);
      setAssetCategories(extractComboListFromResponse(response));
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to fetch asset categories");
      setAssetCategories([]);
    }
  }, []);

  const fetchAssignmentTypes = useCallback(async () => {
    try {
      const response = await getAssignmentTypesCombo(["id", "assignment_type"]);
      setAssignmentTypes(extractComboListFromResponse(response));
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to fetch assignment types");
      setAssignmentTypes([]);
    }
  }, []);

  useEffect(() => {
    fetchAssetTypes();
  }, [fetchAssetTypes]);

  useEffect(() => {
    fetchAssetCategories();
    fetchAssignmentTypes();
  }, [fetchAssetCategories, fetchAssignmentTypes]);

  const handleDelete = async (id) => {
    try {
      await deleteAssetType(id);
      toast.success("Deleted Successfully");

      if (assetTypes.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
        return;
      }

      fetchAssetTypes();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to delete asset type");
    }
  };

  const handleSubmit = async (formData) => {
    const payload = {
      asset_type: (formData?.asset_type || "").trim(),
      asset_category_id: Number(formData?.asset_category_id),
      assignment_type_id: Number(formData?.assignment_type_id),
    };

    if (!payload.asset_type) {
      toast.error("Asset type is required");
      return;
    }

    if (!payload.asset_category_id) {
      toast.error("Asset category is required");
      return;
    }

    if (!payload.assignment_type_id) {
      toast.error("Assignment type is required");
      return;
    }

    try {
      if (editMode && formData?.id) {
        await editAssetType(formData.id, payload);
        toast.success("Updated Successfully");
      } else {
        await addAssetType(payload);
        toast.success("Added Successfully");
      }

      setOpenForm(false);
      setEditMode(false);
      setSelectedType(null);
      fetchAssetTypes();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to save asset type");
    }
  };

  const handleEdit = (item) => {
    setSelectedType(item);
    setEditMode(true);
    setOpenForm(true);
  };

  const handleAdd = () => {
    setSelectedType({
      asset_type: "",
      asset_category_id: "",
      assignment_type_id: "",
    });
    setEditMode(false);
    setOpenForm(true);
  };

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = () => {
    const dummyImportResult = {
      total_rows: 15,
      inserted: 4,
      skipped_duplicate_db: 11,
    };

    const dummyDuplicates = [
      { id: 2, category_name: "Office Equipment" },
      { id: 3, category_name: "Furniture" },
      { id: 4, category_name: "Furniture" },
      { id: 5, category_name: "Furniture" },
      { id: 6, category_name: "Furniture" },
    ];

    setImportResult(dummyImportResult);
    setDuplicateRecords(dummyDuplicates);
    setShowImportModal(true);

    toast.success("Dummy Import Completed");
  };

  const handleUpdateDuplicates = () => {
    toast.success("Duplicate records updated (Frontend)");
    setShowImportModal(false);
  };

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-10">
          <div className="d-flex justify-content-between align-items-center mt-5 mb-3">
            <Header title="Asset Type Management" subtitle="AMS / Asset Types" />

            <div className="d-flex gap-2">
              <button className="btn btn-primary" onClick={handleAdd}>
                + Add Asset Type
              </button>

              <button className="btn btn-info" onClick={handleImportClick}>
                Import
              </button>

              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept=".xls,.xlsx,.csv"
                onChange={handleFileChange}
              />
            </div>
          </div>

          <AssetTypeTable
            assetTypes={assetTypes}
            assetCategories={assetCategories}
            assignmentTypes={assignmentTypes}
            deleteAssetType={handleDelete}
            editAssetType={handleEdit}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onLimitChange={setItemsPerPage}
            onSearch={setSearchTerm}
            searchTerm={searchTerm}
            loading={loading}
            totalCount={totalCount}
          />

          {openForm && (
            <AssetTypeForm
              data={selectedType}
              add={handleSubmit}
              close={() => setOpenForm(false)}
              editMode={editMode}
              assetCategories={assetCategories}
              assignmentTypes={assignmentTypes}
            />
          )}

          {showImportModal && importResult && (
            <div className="modal fade show d-block">
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Import Result</h5>
                    <button
                      className="btn-close"
                      onClick={() => setShowImportModal(false)}
                    ></button>
                  </div>

                  <div className="modal-body">
                    <p><strong>Total Rows:</strong> {importResult.total_rows}</p>
                    <p><strong>Inserted:</strong> {importResult.inserted}</p>
                    <p><strong>Duplicate in DB:</strong> {importResult.skipped_duplicate_db}</p>

                    {duplicateRecords.length > 0 && (
                      <>
                        <h6 className="text-danger mt-3">Duplicate Records Found</h6>

                        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                          <table className="table table-bordered table-striped">
                            <thead className="table-light">
                              <tr>
                                <th>S.No</th>
                                <th>ID</th>
                                <th>Asset Category</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {duplicateRecords.map((row, index) => (
                                <tr key={row.id}>
                                  <td>{index + 1}</td>
                                  <td>{row.id}</td>
                                  <td>{row.category_name}</td>
                                  <td>
                                    <span className="text-danger fw-bold">Duplicate</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button className="btn btn-success" onClick={handleUpdateDuplicates}>
                      Update
                    </button>

                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowImportModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetType;
